// src/components/appointments/AppointmentDetail.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Edit,
  FileText,
  MessageSquare,
  Activity,
  DollarSign,
  Check,
  X,
  Trash2,
  AlertTriangle,
  Plus,
  Lock,
  Eye,
  StickyNote,
  Image as ImageIcon,
  Paperclip,
  Loader2,
  Edit2,
  MoreVertical,
  ChevronsUpDown,
  ChevronDown
} from 'lucide-react';

// Import shadcn components
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';

// Import custom components
import PageContainer from '../ui/layout/PageContainer/PageContainer';
import EventStatusBadge from '../ui/calendar/events/EventStatusBadge/EventStatusBadge';

// Import hooks
import { useEvents } from '../../hooks/useEvents';
import { useCustomers } from '../../hooks/useCustomers';
// TODO: Refactor to use new Notes system (useNotes hook)
// import { useNotes } from '../../hooks/useNotes';
import { useInvoices } from '../../hooks/useInvoices';
import { useSettings } from '../../hooks/useSettings';
import { cn } from '../../utils/cn';

const STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pending',     color: 'text-amber-400',   bg: 'bg-amber-500/20',   border: 'border-amber-500/30' },
  { value: 'confirmed',   label: 'Confirmed',   color: 'text-blue-400',    bg: 'bg-blue-500/20',    border: 'border-blue-500/30' },
  { value: 'in_progress', label: 'In Progress', color: 'text-purple-400',  bg: 'bg-purple-500/20',  border: 'border-purple-500/30' },
  { value: 'completed',   label: 'Completed',   color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' },
  { value: 'cancelled',   label: 'Cancelled',   color: 'text-red-400',     bg: 'bg-red-500/20',     border: 'border-red-500/30' },
];

const AppointmentStatusDropdown = ({ status, onSelect, disabled }) => {
  const current = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  const isCancelled = status === 'cancelled';

  if (isCancelled) {
    return (
      <span className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium shrink-0 mt-1.5",
        current.color, current.bg, current.border
      )}>
        {current.label}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button className={cn(
          "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors shrink-0 mt-1.5",
          current.color, current.bg, current.border,
          "hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
        )}>
          <span>{current.label}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {STATUS_OPTIONS.filter(opt => opt.value !== 'cancelled').map(opt => (
          <DropdownMenuItem
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            className={cn("flex items-center gap-2", opt.value === status && "font-semibold")}
          >
            <span className={cn("w-2 h-2 rounded-full flex-shrink-0", opt.bg, "border", opt.border)} />
            <span>{opt.label}</span>
            {opt.value === status && <Check className="w-3.5 h-3.5 ml-auto opacity-60" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          onClick={() => onSelect('cancelled')}
          className="flex items-center gap-2 text-red-400 focus:text-red-400"
        >
          <span className={cn("w-2 h-2 rounded-full flex-shrink-0 bg-red-500/20 border border-red-500/30")} />
          <span>Cancel Appointment</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AppointmentDetail = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  // Hooks for data access
  const {
    events,
    selectedEvent,
    selectedEventLoading,
    selectedEventError,
    loadEventDetails,
    deleteEvent,
    updateEvent,
    updateEventStatus,
    clearSelectedEvent,
    getEventById,
    isConnected
  } = useEvents();

  const { getCustomerById } = useCustomers();

  // TODO: Refactor to use new Notes system
  // Placeholder values until migration is complete
  const loadEventNotes = () => {};
  const getNotesForEvent = () => [];
  const getPhotoCountForEvent = () => 0;
  const createNote = async () => {};
  const updateNote = async () => {};
  const deleteNote = async () => {};
  const createNoteWithPhotos = async () => {};
  const notesLoading = false;
  const createNoteLoading = false;
  const updateNoteLoading = false;
  const deleteNoteLoading = false;
  const fileUploading = false;
  const getPhotoUrl = () => '';

  const { getCustomerInvoices, loadInvoices, invoicesLoading } = useInvoices();
  const { services, loadServices, loading: loadingServices } = useSettings();

  // Local state
  const [activeSection, setActiveSection] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Appointment editing state
  const [isEditingAppointment, setIsEditingAppointment] = useState(false);
  const [editedAppointment, setEditedAppointment] = useState({});
  const [serviceComboOpen, setServiceComboOpen] = useState(false);

  // Notes state
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteType, setNewNoteType] = useState('internal');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [expandedPhotos, setExpandedPhotos] = useState({});

  // Section refs for scroll-to functionality
  const sectionRefs = {
    overview: useRef(null),
    customer: useRef(null),
    notes: useRef(null),
    activity: useRef(null),
    invoices: useRef(null)
  };

  // Store calculated section positions
  const sectionPositions = useRef({});

  // Get appointment - prioritize selectedEvent for real-time updates
  const appointment = useMemo(() => {
    // Always prefer selectedEvent when it matches - this ensures real-time updates work
    if (selectedEvent?.id === appointmentId) {
      return selectedEvent;
    }
    // Fallback to cache search if selectedEvent doesn't match
    // Including events in deps ensures this recalculates when any event updates
    return getEventById(appointmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, appointmentId, getEventById, events]);

  // Get customer data
  const customer = useMemo(() => {
    if (appointment?.customer_id) {
      return getCustomerById(appointment.customer_id);
    }
    return null;
  }, [appointment?.customer_id, getCustomerById]);

  // Get notes data
  const notes = useMemo(() => {
    if (appointmentId) {
      return getNotesForEvent(appointmentId);
    }
    return [];
  }, [appointmentId, getNotesForEvent]);

  const photoCount = useMemo(() => {
    if (appointmentId) {
      return getPhotoCountForEvent(appointmentId);
    }
    return 0;
  }, [appointmentId, getPhotoCountForEvent]);

  // Get customer invoices
  const customerInvoices = useMemo(() => {
    if (appointment?.customer_id) {
      return getCustomerInvoices(appointment.customer_id);
    }
    return [];
  }, [appointment?.customer_id, getCustomerInvoices]);

  // Service options for dropdown with edge case handling
  const serviceOptions = useMemo(() => {
    const options = services.map(service => ({
      value: service.id,
      label: service.name,
      description: service.description,
      duration: service.duration_minutes,
      price: service.price
    }));

    // Edge case: If current service type doesn't match any service, add it as "(Custom)"
    const serviceType = appointment?.title;
    if (serviceType && !services.some(s => s.name === serviceType)) {
      options.unshift({
        value: 'custom',
        label: `${serviceType} (Custom)`,
        description: 'Service no longer available',
        isCustom: true
      });
    }

    return options;
  }, [services, appointment?.title]);

  // Load appointment details on mount
  useEffect(() => {
    if (isConnected && appointmentId && !appointment) {
      console.log('Loading appointment details for:', appointmentId);
      loadEventDetails(appointmentId);
    }
  }, [isConnected, appointmentId, appointment, loadEventDetails]);

  // Load job notes when appointment loads
  useEffect(() => {
    if (isConnected && appointmentId && appointment) {
      console.log('Loading job notes for appointment:', appointmentId);
      loadEventNotes(appointmentId);
    }
  }, [isConnected, appointmentId, appointment, loadEventNotes]);

  // Load invoices when appointment loads
  useEffect(() => {
    if (isConnected && appointment?.customer_id) {
      console.log('Loading invoices for customer:', appointment.customer_id);
      loadInvoices();
    }
  }, [isConnected, appointment?.customer_id, loadInvoices]);

  // Load services for service type dropdown
  useEffect(() => {
    if (isConnected && services.length === 0) {
      console.log('Loading services for appointment editing');
      loadServices();
    }
  }, [isConnected, services.length, loadServices]);

  // Clear selected event when component unmounts
  useEffect(() => {
    return () => {
      clearSelectedEvent();
    };
  }, [clearSelectedEvent]);

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Calculate section positions once when appointment loads
  useEffect(() => {
    if (!appointment) return;

    const calculatePositions = () => {
      const contentDiv = sectionRefs.overview?.current?.parentElement;
      if (!contentDiv) return;

      const containerTop = contentDiv.scrollTop;
      const positions = {};

      Object.entries(sectionRefs).forEach(([id, ref]) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const parentRect = contentDiv.getBoundingClientRect();
          positions[id] = rect.top - parentRect.top + containerTop;
        }
      });

      sectionPositions.current = positions;
    };

    setTimeout(calculatePositions, 100);
  }, [appointment]);

  // Scroll spy and header collapse
  useEffect(() => {
    if (!appointment) return;

    const contentDiv = sectionRefs.overview?.current?.parentElement;
    if (!contentDiv) return;

    const handleScroll = () => {
      const scrollTop = contentDiv.scrollTop;
      const scrollHeight = contentDiv.scrollHeight;
      const clientHeight = contentDiv.clientHeight;
      const positions = sectionPositions.current;

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isAtBottom) {
        setActiveSection(prev => prev !== 'invoices' ? 'invoices' : prev);
        return;
      }

      const sectionOrder = ['invoices', 'activity', 'notes', 'customer', 'overview'];

      for (const sectionId of sectionOrder) {
        const sectionTop = positions[sectionId];
        if (sectionTop !== undefined && scrollTop >= sectionTop - 100) {
          setActiveSection(prev => prev !== sectionId ? sectionId : prev);
          break;
        }
      }
    };

    contentDiv.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      contentDiv.removeEventListener('scroll', handleScroll);
    };
  }, [appointment]);

  // Helper functions
  const getInitials = (name) => {
    if (!name) return 'UC';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parseFloat(amount || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getTimezoneOffset = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset <= 0 ? '+' : '-';
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const convertToISOWithTimezone = (datetimeLocal) => {
    // Convert datetime-local format (YYYY-MM-DDTHH:mm) to ISO with timezone
    // Example: "2025-12-16T14:30" -> "2025-12-16T14:30:00-06:00"
    if (!datetimeLocal) return datetimeLocal;
    const timezoneOffset = getTimezoneOffset();
    return `${datetimeLocal}:00${timezoneOffset}`;
  };

  // Event handlers
  const handleBack = () => {
    clearSelectedEvent();
    navigate('/appointments');
  };

  const handleEdit = () => {
    // Enter edit mode and initialize edited fields with current values
    setIsEditingAppointment(true);
    setEditedAppointment({
      title: serviceType,
      duration_minutes: duration,
      scheduled_datetime: scheduledDateTime,
      service_address: serviceAddress,
      description: description
    });
  };

  const handleSaveAppointment = () => {
    // Build updates object with only changed fields
    const updates = {};

    if (editedAppointment.title !== serviceType) {
      updates.title = editedAppointment.title;
    }
    if (editedAppointment.duration_minutes !== duration) {
      updates.duration_minutes = editedAppointment.duration_minutes;
    }
    if (editedAppointment.scheduled_datetime !== scheduledDateTime) {
      // Convert datetime-local format to ISO with timezone before sending
      updates.scheduled_datetime = convertToISOWithTimezone(editedAppointment.scheduled_datetime);
    }
    if (editedAppointment.service_address !== serviceAddress) {
      updates.service_address = editedAppointment.service_address;
    }
    if (editedAppointment.description !== description) {
      updates.description = editedAppointment.description;
    }

    // Only update if there are actual changes
    if (Object.keys(updates).length > 0) {
      console.log('Updating appointment with field-level changes:', updates);
      updateEvent(appointmentId, updates);
    }

    // Exit edit mode
    setIsEditingAppointment(false);
    setEditedAppointment({});
  };

  const handleCancelAppointmentEdit = () => {
    // Discard changes and exit edit mode
    setIsEditingAppointment(false);
    setEditedAppointment({});
  };

  const handleServiceSelect = (serviceId) => {
    if (serviceId === 'custom') return; // Can't change to custom service

    const service = services.find(s => s.id === serviceId);
    if (service) {
      setEditedAppointment({
        ...editedAppointment,
        title: service.name,
        duration_minutes: service.duration_minutes || editedAppointment.duration_minutes
      });
    }
    setServiceComboOpen(false);
  };

  const handleStatusUpdate = async (newStatus, statusData = {}) => {
    try {
      setSaving(true);
      updateEventStatus(appointmentId, newStatus, statusData);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        setSaving(true);
        updateEventStatus(appointmentId, 'cancelled');
      } catch (error) {
        console.error('Failed to cancel appointment:', error);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setSaving(true);
      deleteEvent(appointmentId);
      navigate('/appointments');
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      setSaving(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Notes handlers
  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;

    const noteData = {
      event_id: appointmentId,
      note_type: newNoteType,
      content: newNoteContent.trim()
    };

    if (selectedFiles.length > 0) {
      await createNoteWithPhotos(appointmentId, noteData, selectedFiles);
    } else {
      await createNote(appointmentId, noteData);
    }

    setNewNoteContent('');
    setNewNoteType('internal');
    setSelectedFiles([]);
    setIsAddingNote(false);
  };

  const handleEditNote = async (noteId) => {
    if (!editContent.trim()) return;
    await updateNote(noteId, { content: editContent.trim() });
    setEditingNote(null);
    setEditContent('');
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;
    await deleteNote(noteToDelete);
    setNoteToDelete(null);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const startEdit = (note) => {
    setEditingNote(note.id);
    setEditContent(note.content);
  };

  const cancelEdit = () => {
    setEditingNote(null);
    setEditContent('');
  };

  const togglePhotoExpansion = (noteId) => {
    setExpandedPhotos(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  // Connection status warning
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Connection Required</h2>
          <p className="text-muted-foreground mb-6">
            Please check your internet connection and try again.
          </p>
          <Button onClick={handleBack}>
            Back to Appointments
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Loading state
  if (selectedEventLoading && !appointment) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading appointment details...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (selectedEventError && !appointment) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{selectedEventError}</AlertDescription>
        </Alert>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Unable to Load Appointment</h2>
          <p className="text-muted-foreground mb-6">{selectedEventError}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => loadEventDetails(appointmentId)}>
              Try Again
            </Button>
            <Button variant="outline" onClick={handleBack}>
              Back to Appointments
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Appointment not found
  if (!selectedEventLoading && !appointment) {
    return (
      <PageContainer>
        <div className="text-center py-16">
          <h2 className="text-2xl font-bold mb-2">Appointment Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The appointment you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={handleBack}>
            Back to Appointments
          </Button>
        </div>
      </PageContainer>
    );
  }

  const appointmentTitle = appointment?.title || 'Appointment';
  const serviceType = appointment?.service_type || 'Service';
  const scheduledDateTime = appointment?.scheduled_datetime;
  const duration = appointment?.duration_minutes || 180;
  const status = appointment?.status || 'pending';
  const serviceAddress = appointment?.service_address || '';
  const description = appointment?.description || '';
  const smsConsent = appointment?.sms_consent || false;

  // Navigation sections
  const navSections = [
    { id: 'overview', label: 'Overview' },
    { id: 'customer', label: 'Customer' },
    { id: 'notes', label: 'Notes' },
    { id: 'activity', label: 'Activity' },
    ...(customerInvoices.length > 0 ? [{ id: 'invoices', label: 'Invoices' }] : [])
  ];

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full pt-12 pl-36 pr-36 flex flex-col">
            {/* Action Buttons */}
            <div className="flex justify-end mb-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>

            {/* Static header */}
            <div className="mb-6">
              {/* Title + status dropdown */}
              <div className="flex items-start gap-4 mb-2">
                <h1 className="text-4xl font-bold text-foreground truncate">
                  {appointmentTitle}
                </h1>
                <AppointmentStatusDropdown
                  status={status}
                  onSelect={(newStatus) => {
                    if (newStatus === 'cancelled') setShowCancelConfirm(true);
                    else handleStatusUpdate(newStatus);
                  }}
                  disabled={saving}
                />
              </div>
              {/* Date/time row */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(scheduledDateTime)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  <span>{formatTime(scheduledDateTime)} • {duration} min</span>
                </div>
              </div>
            </div>

            {/* Error display */}
            {selectedEventError && appointment && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{selectedEventError}</AlertDescription>
              </Alert>
            )}

            {/* Side Nav and Content */}
            <div className="flex-1 flex min-h-0">
              {/* Side Navigation */}
              <div className="w-40 flex-shrink-0">
                <div className="space-y-1 sticky top-0">
                  {navSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        "block w-full text-left text-sm py-1 transition-colors",
                        activeSection === section.id
                          ? "text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content - scrollable */}
              <div className="flex-1 min-w-0 overflow-y-auto pl-2 space-y-10 pb-12">
                {/* Overview Section */}
                <div ref={sectionRefs.overview} data-section="overview" className="scroll-mt-6 space-y-5">
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                        <Calendar className="w-5 h-5" />
                        Appointment Details
                      </h3>

                      {/* Edit / Save / Cancel buttons */}
                      {!isEditingAppointment ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleEdit}
                          className="flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleSaveAppointment}
                          >
                            Save Changes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelAppointmentEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Complete details about this appointment including timing, location, and service information
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Service Type</Label>
                      {!isEditingAppointment ? (
                        <p className="text-base">{serviceType}</p>
                      ) : (
                        <Popover open={serviceComboOpen} onOpenChange={setServiceComboOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={serviceComboOpen}
                              className="w-full justify-between bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] hover:bg-zinc-800"
                            >
                              {editedAppointment.title
                                ? serviceOptions.find(s => s.label === editedAppointment.title)?.label || editedAppointment.title
                                : "Select service type..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search service..." />
                              <CommandList>
                                <CommandEmpty>No service found.</CommandEmpty>
                                <CommandGroup>
                                  {serviceOptions.map((service) => (
                                    <CommandItem
                                      key={service.value}
                                      value={service.label}
                                      onSelect={() => handleServiceSelect(service.value)}
                                      disabled={service.isCustom}
                                    >
                                      <div className="flex-1">
                                        <div className="font-medium">{service.label}</div>
                                        {service.description && (
                                          <div className="text-sm text-muted-foreground">{service.description}</div>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-end text-xs text-muted-foreground ml-2">
                                        {service.duration && <span>{service.duration} min</span>}
                                        {service.price && <span className="text-emerald-400">${service.price}</span>}
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Duration</Label>
                      {!isEditingAppointment ? (
                        <p className="text-base">{duration} minutes</p>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={editedAppointment.duration_minutes || ''}
                            onChange={(e) => setEditedAppointment({...editedAppointment, duration_minutes: parseInt(e.target.value) || 0})}
                            placeholder="Duration"
                            min="0"
                            className="flex-1 bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                          <span className="text-sm text-muted-foreground">minutes</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Scheduled Date & Time</Label>
                      {!isEditingAppointment ? (
                        <p className="text-base">{formatDateTime(scheduledDateTime)}</p>
                      ) : (
                        <Input
                          type="datetime-local"
                          value={formatDateTimeForInput(editedAppointment.scheduled_datetime)}
                          onChange={(e) => setEditedAppointment({...editedAppointment, scheduled_datetime: e.target.value})}
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Status</Label>
                      <div className="flex items-center">
                        <EventStatusBadge status={status} size="sm" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Service Location
                    </Label>
                    {!isEditingAppointment ? (
                      <p className="text-base text-muted-foreground">{serviceAddress || 'N/A'}</p>
                    ) : (
                      <Input
                        value={editedAppointment.service_address || ''}
                        onChange={(e) => setEditedAppointment({...editedAppointment, service_address: e.target.value})}
                        placeholder="Service location address"
                        className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Appointment Notes</Label>
                    {!isEditingAppointment ? (
                      <p className="text-base text-muted-foreground">{description || 'N/A'}</p>
                    ) : (
                      <Textarea
                        value={editedAppointment.description || ''}
                        onChange={(e) => setEditedAppointment({...editedAppointment, description: e.target.value})}
                        placeholder="Additional notes or details about the appointment"
                        rows={3}
                        className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground pt-2">
                    <div>
                      <span className="font-medium">Created:</span> {formatDateTime(appointment?.created_at)}
                    </div>
                    <div>
                      <span className="font-medium">Last Updated:</span> {formatDateTime(appointment?.updated_at)}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Customer Section */}
                {customer && (
                  <>
                    <div ref={sectionRefs.customer} data-section="customer" className="scroll-mt-6 space-y-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                            <User className="w-5 h-5" />
                            Customer Information
                          </h3>
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            Contact details and profile for the customer associated with this appointment
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/customers/${customer.id}`)}
                        >
                          View Full Profile
                        </Button>
                      </div>

                      <div className="flex items-start gap-6">
                        <Avatar className="h-20 w-20 shrink-0">
                          <AvatarFallback className="text-2xl font-semibold">
                            {getInitials(customer.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-4">
                          <div>
                            <h4 className="text-xl font-semibold mb-1">{customer.name}</h4>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            {customer.email && (
                              <div className="space-y-1">
                                <Label className="text-sm font-medium flex items-center gap-2">
                                  <Mail className="w-4 h-4" />
                                  Email
                                </Label>
                                <p className="text-base text-muted-foreground">{customer.email}</p>
                              </div>
                            )}
                            {customer.phone && (
                              <div className="space-y-1">
                                <Label className="text-sm font-medium flex items-center gap-2">
                                  <Phone className="w-4 h-4" />
                                  Phone
                                </Label>
                                <p className="text-base text-muted-foreground">{customer.phone}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />
                  </>
                )}

                {/* Notes Section */}
                <div ref={sectionRefs.notes} data-section="notes" className="scroll-mt-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                        <MessageSquare className="w-5 h-5" />
                        Notes & Photos
                        {(notes.length > 0 || photoCount > 0) && (
                          <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                            {notes.length} notes • {photoCount} photos
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        Internal and external notes with attached photos for this appointment
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setIsAddingNote(true)}
                      disabled={isAddingNote}
                      className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 shadow-[0_2px_4px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_6px_16px_rgba(255,255,255,0.15),0_4px_8px_rgba(0,0,0,0.5)] hover:border-zinc-500 hover:-translate-y-1 transition-all duration-200 font-medium"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Note
                    </Button>
                  </div>

                  {/* Add Note Form */}
                  {isAddingNote && (
                    <div className="p-4 rounded-lg border border-border bg-zinc-900/50 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">New Note</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setIsAddingNote(false);
                            setNewNoteContent('');
                            setSelectedFiles([]);
                          }}
                          className="h-7 w-7 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Note Type Toggle */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Type:</span>
                        <Button
                          variant={newNoteType === 'internal' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNewNoteType('internal')}
                        >
                          <Lock className="w-3 h-3 mr-1" />
                          Internal
                        </Button>
                        <Button
                          variant={newNoteType === 'external' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNewNoteType('external')}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          External
                        </Button>
                      </div>

                      {/* Note Content */}
                      <Textarea
                        placeholder="Enter note content..."
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        rows={4}
                        className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                      />

                      {/* File Selection */}
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('file-upload').click()}
                          disabled={fileUploading}
                        >
                          <Paperclip className="w-4 h-4 mr-2" />
                          Attach Photos
                        </Button>
                        <input
                          id="file-upload"
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                        />

                        {/* Selected Files */}
                        {selectedFiles.length > 0 && (
                          <div className="space-y-1">
                            {selectedFiles.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-zinc-900/50 border border-zinc-700 rounded px-3 py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                                  <span className="text-sm">{file.name}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeSelectedFile(index)}
                                  className="h-7 w-7 p-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsAddingNote(false);
                            setNewNoteContent('');
                            setSelectedFiles([]);
                          }}
                          disabled={createNoteLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddNote}
                          disabled={!newNoteContent.trim() || createNoteLoading}
                          className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 shadow-[0_2px_4px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_6px_16px_rgba(255,255,255,0.15),0_4px_8px_rgba(0,0,0,0.5)] hover:border-zinc-500 hover:-translate-y-1 transition-all duration-200 font-medium"
                        >
                          {createNoteLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Create Note
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Notes List */}
                  {notesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : notes.length === 0 ? (
                    <div className="text-center py-12">
                      <StickyNote className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm">
                        No notes yet. Add your first note to get started.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((note) => (
                        <div
                          key={note.id}
                          className="p-4 rounded-lg border border-border bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors"
                        >
                          {/* Note Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={note.note_type === 'internal' ? 'secondary' : 'outline'}
                                className={
                                  note.note_type === 'internal'
                                    ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                    : 'bg-green-500/20 text-green-300 border-green-500/30'
                                }
                              >
                                {note.note_type === 'internal' ? (
                                  <>
                                    <Lock className="w-3 h-3 mr-1" />
                                    Internal
                                  </>
                                ) : (
                                  <>
                                    <Eye className="w-3 h-3 mr-1" />
                                    External
                                  </>
                                )}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatDateTime(note.created_at)}
                              </span>
                            </div>

                            {/* Actions Dropdown */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => startEdit(note)}>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setNoteToDelete(note.id)}
                                  className="text-red-400"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* Note Content */}
                          {editingNote === note.id ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                rows={4}
                                className="bg-zinc-900 border border-zinc-700/50"
                              />
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={cancelEdit}>
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleEditNote(note.id)}
                                  disabled={!editContent.trim() || updateNoteLoading}
                                >
                                  {updateNoteLoading ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Saving...
                                    </>
                                  ) : (
                                    'Save'
                                  )}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                          )}

                          {/* Photos */}
                          {note.photos && note.photos.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePhotoExpansion(note.id)}
                                className="text-muted-foreground hover:text-foreground h-7 px-2"
                              >
                                <ImageIcon className="w-4 h-4 mr-2" />
                                {note.photos.length} {note.photos.length === 1 ? 'Photo' : 'Photos'}
                              </Button>

                              {expandedPhotos[note.id] && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {note.photos.map((photo) => (
                                    <div
                                      key={photo.id}
                                      className="relative group aspect-square rounded-lg overflow-hidden bg-zinc-900/50 border border-zinc-700"
                                    >
                                      <img
                                        src={getPhotoUrl(photo)}
                                        alt={photo.caption || 'Job photo'}
                                        className="w-full h-full object-cover"
                                      />
                                      {photo.caption && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2">
                                          <p className="text-xs text-white truncate">{photo.caption}</p>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Delete Confirmation Dialog */}
                  <AlertDialog open={!!noteToDelete} onOpenChange={() => setNoteToDelete(null)}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Note?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete the note and all its attached photos.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteNote}
                          disabled={deleteNoteLoading}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {deleteNoteLoading ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                <Separator />

                {/* Activity Section */}
                <div ref={sectionRefs.activity} data-section="activity" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Activity className="w-5 h-5" />
                      Activity Timeline
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Track status changes and important events throughout this appointment's lifecycle
                    </p>
                  </div>

                  {/* Generate timeline events */}
                  {(() => {
                    const timelineEvents = [];

                    // Add status-based events
                    if (appointment.status === 'cancelled') {
                      timelineEvents.push({
                        id: 'cancelled',
                        title: 'Appointment Cancelled',
                        description: appointment.status_notes || 'Appointment was cancelled',
                        timestamp: appointment.updated_at || appointment.created_at,
                        icon: X,
                        color: 'text-red-400',
                        bgColor: 'bg-red-400/10',
                        borderColor: 'border-red-400/30'
                      });
                    } else if (appointment.status === 'completed') {
                      timelineEvents.push({
                        id: 'completed',
                        title: 'Appointment Completed',
                        description: appointment.status_notes || 'Service was completed successfully',
                        timestamp: appointment.updated_at || appointment.created_at,
                        icon: Check,
                        color: 'text-green-400',
                        bgColor: 'bg-green-400/10',
                        borderColor: 'border-green-400/30'
                      });
                    } else if (appointment.status === 'in_progress') {
                      timelineEvents.push({
                        id: 'in_progress',
                        title: 'Work Started',
                        description: appointment.status_notes || 'Service work is currently in progress',
                        timestamp: appointment.updated_at || appointment.created_at,
                        icon: Activity,
                        color: 'text-blue-400',
                        bgColor: 'bg-blue-400/10',
                        borderColor: 'border-blue-400/30'
                      });
                    } else if (appointment.status === 'confirmed') {
                      timelineEvents.push({
                        id: 'confirmed',
                        title: 'Appointment Confirmed',
                        description: appointment.status_notes || 'Appointment has been confirmed',
                        timestamp: appointment.updated_at || appointment.created_at,
                        icon: Check,
                        color: 'text-green-400',
                        bgColor: 'bg-green-400/10',
                        borderColor: 'border-green-400/30'
                      });
                    }

                    // Add creation event
                    timelineEvents.push({
                      id: 'created',
                      title: 'Appointment Created',
                      description: `Scheduled for ${formatDateTime(appointment.scheduled_datetime)}`,
                      timestamp: appointment.created_at,
                      icon: Calendar,
                      color: 'text-slate-400',
                      bgColor: 'bg-slate-400/10',
                      borderColor: 'border-slate-400/30'
                    });

                    // Sort by timestamp (newest first)
                    timelineEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                    return (
                      <div className="relative pl-8">
                        {/* Timeline Line */}
                        <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />

                        {/* Timeline Items */}
                        <div className="space-y-4">
                          {timelineEvents.map((event, index) => {
                            const Icon = event.icon;
                            const isLast = index === timelineEvents.length - 1;

                            return (
                              <div
                                key={event.id}
                                className="relative group"
                              >
                                {/* Timeline Dot */}
                                <div
                                  className={cn(
                                    "absolute -left-8 top-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all",
                                    event.borderColor,
                                    event.bgColor,
                                    isLast && "ring-4 ring-border/50"
                                  )}
                                >
                                  <Icon className={cn("w-4 h-4", event.color)} />
                                </div>

                                {/* Event Content */}
                                <div className="p-4 rounded-lg border border-border bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors">
                                  <div className="flex items-start justify-between mb-2">
                                    <h4 className="text-sm font-semibold">{event.title}</h4>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                                      {formatDateTime(event.timestamp)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{event.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Invoices Section */}
                {customerInvoices.length > 0 && (
                  <>
                    <Separator />
                    <div ref={sectionRefs.invoices} data-section="invoices" className="scroll-mt-6 space-y-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                            <DollarSign className="w-5 h-5" />
                            Customer Invoices
                            <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                              {customerInvoices.length}
                            </span>
                          </h3>
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            All invoices associated with this customer
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/invoices/create?customer_id=${appointment?.customer_id}`)}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          New Invoice
                        </Button>
                      </div>

                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Due Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoicesLoading ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-8">
                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                <p className="text-sm text-muted-foreground">Loading invoices...</p>
                              </TableCell>
                            </TableRow>
                          ) : (
                            customerInvoices.map((invoice) => (
                              <TableRow
                                key={invoice.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => navigate(`/invoices/${invoice.id}`)}
                              >
                                <TableCell className="font-medium">#{invoice.invoice_number}</TableCell>
                                <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                                <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : '—'}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant={invoice.status === 'paid' ? 'default' : invoice.status === 'overdue' ? 'destructive' : 'outline'}
                                    className={cn(
                                      invoice.status === 'paid' && 'bg-green-500/20 text-green-300 border-green-500/30',
                                      invoice.status === 'overdue' && 'bg-red-500/20 text-red-300 border-red-500/30',
                                      invoice.status === 'sent' && 'bg-blue-500/20 text-blue-300 border-blue-500/30',
                                      invoice.status === 'draft' && 'bg-slate-500/20 text-slate-300 border-slate-500/30'
                                    )}
                                  >
                                    {invoice.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(invoice.total_amount)}</TableCell>
                                <TableCell className="text-right">
                                  {invoice.balance_due > 0 ? (
                                    <span className="text-amber-400">{formatCurrency(invoice.balance_due)}</span>
                                  ) : (
                                    <span className="text-emerald-400">Paid</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}

                <Separator />

                {/* Danger Zone */}
                <div className="space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-red-400">
                      <AlertTriangle className="w-5 h-5" />
                      Danger Zone
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Permanently delete this appointment and all associated data
                    </p>
                  </div>

                  {showDeleteConfirm ? (
                    <div className="flex flex-col gap-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-sm text-foreground">
                        Are you sure you want to delete this appointment? This action cannot be undone.
                      </p>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelDelete}
                          disabled={saving}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleConfirmDelete}
                          disabled={saving}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {saving ? 'Deleting...' : 'Delete Appointment'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={saving}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Appointment
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Cancel Appointment Confirmation */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-500"
              onClick={() => { handleStatusUpdate('cancelled'); setShowCancelConfirm(false); }}
            >
              Yes, Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default AppointmentDetail;
